/**
 * Server-backed coin wallet (handegg-accounts-spec.md §2–§5) behind the same synchronous `LocalWallet` interface
 * the Store / Pick Head / Results already use (`@hf/shared` wallet.ts), so no screen changes.
 *
 *   local mode   before this device's account ever synced (first launch offline, server down, accounts off): the
 *                plain local wallet, exactly as before.
 *   server mode  after the first sync: the server's balance / unlocks / daily counters, cached in storage so reads
 *                stay synchronous and work offline. On the switch the pre-account local wallet is imported ONCE
 *                (`POST /wallet/migrate`, server-capped).
 *
 * In server mode every mutation is optimistic on the cached snapshot and confirmed by the server, whose answer
 * replaces the snapshot (it is the truth): unlocks → `POST /shop/unlock`, vs-CPU payouts → `POST /rewards/bot`
 * (capped server-side), online payouts are made by the server itself from the lobby's verdict — the client only
 * estimates them for the Results screen and then reads the real amount back (`confirmOnlineReward`). Mutations
 * made offline are queued (persisted) and replayed on the next sync; both are idempotent server-side.
 */
import {
  BALL_SKIN_CATALOG,
  BALL_SKIN_IDS,
  CHARACTER_CATALOG,
  CHARACTER_IDS,
  botMatchReward,
  isBallSkinId,
  isCharacterId,
  isStarterBallSkin,
  isStarterCharacter,
  onlineMatchReward,
  type LocalWallet,
  type WalletDailyStatus,
} from '@hf/shared';
import type { KeyValueStorage } from '../platform';
import type { AccountClient } from './account-client';

export type WalletMode = 'local' | 'server';

interface SnapshotDaily {
  day: string;
  botMatchesRewardedToday: number;
  onlineWinStreak: number;
  onlineFirstWinClaimedToday: boolean;
}

interface Snapshot {
  userId: string;
  balance: number;
  owned: string[];
  daily: SnapshotDaily;
}

type PendingOp =
  | { kind: 'unlock'; sku: string; idem: string }
  | { kind: 'bot'; matchId: string; won: boolean; durationSec: number };

interface ServerWallet {
  balance: number;
  entitlements: string[];
  daily: WalletDailyStatus;
  migrated?: boolean;
  recent?: { delta: number; ref_type: string | null; ref_id: string | null; created_at: number }[];
}

export type PurchaseCreditResult =
  /** No account (accounts off): the caller credits the local wallet itself, as before. */
  | { readonly status: 'local' }
  /** The server verified the transaction and credited it now (or had already: `duplicate`). Finish it. */
  | { readonly status: 'credited' | 'duplicate'; readonly coins: number }
  /** Server unreachable: do NOT finish the transaction — StoreKit replays it on the next launch. */
  | { readonly status: 'retry' }
  /** The server refused the transaction (verification / unknown product). */
  | { readonly status: 'rejected'; readonly error: string };

export interface SyncedWallet extends LocalWallet {
  /** Credit an App Store consumable through the server (`POST /iap/apple`, server-side StoreKit verification). */
  creditAppleTransaction(jws: string): Promise<PurchaseCreditResult>;
  mode(): WalletMode;
  /** Pull the server wallet (bootstrapping the account / importing the local wallet the first time) and replay
   *  queued offline mutations. Resolves false when the server could not be reached (nothing changes). */
  sync(): Promise<boolean>;
  /** Server mode: the coins the server actually paid for an online game (null: unknown / local mode). */
  confirmOnlineReward(matchId: string): Promise<number | null>;
  /** Online payout estimate (server mode) or local credit (local mode). `authed`: the lobby socket carried a token;
   *  `durationSec`: how long the game ran (the server pays nothing under ONLINE_MIN_DURATION_SEC). */
  rewardOnlineMatch(
    won: boolean,
    opts?: { readonly forfeit?: boolean; readonly authed?: boolean; readonly durationSec?: number },
  ): number;
  /**
   * After `AccountClient.deleteAccount()`: drop the server snapshot + pending queue, reset the local wallet, and
   * sync into the fresh guest (or stay local if offline). Always safe to call; never throws.
   */
  resetAfterDelete(): Promise<void>;
}

export interface SyncedWalletOptions {
  readonly local: LocalWallet;
  readonly storage: KeyValueStorage | null;
  /** null = accounts disabled (feature flag / no server URL): a plain local wallet with no-op sync. */
  readonly account: AccountClient | null;
  /** Something visible changed (balance, owned, daily): hosts refresh their signals. */
  readonly onChange?: () => void;
  readonly random?: () => number;
  readonly now?: () => number;
}

const SNAP_KEY = 'hf.wallet.server.v1';
const QUEUE_KEY = 'hf.wallet.pending.v1';
/** Mirrors the server's ONLINE_MIN_DURATION_SEC (apps/server-cf account-do.ts). */
const ONLINE_MIN_DURATION_SEC = 45;

const utcDay = (t: number): string => new Date(t).toISOString().slice(0, 10);

const isFree = (sku: string): boolean =>
  (isCharacterId(sku) && isStarterCharacter(sku)) || (isBallSkinId(sku) && isStarterBallSkin(sku));

/** Every sku the local wallet owns beyond the free starters (what the one-time import sends). */
const localOwned = (w: LocalWallet): string[] =>
  [...CHARACTER_IDS, ...BALL_SKIN_IDS].filter((sku) => !isFree(sku) && w.owns(sku));

const readJson = <T>(s: KeyValueStorage | null, key: string): T | null => {
  try {
    const raw = s?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};
const writeJson = (s: KeyValueStorage | null, key: string, v: unknown): void => {
  try {
    s?.setItem(key, JSON.stringify(v));
  } catch {
    /* ignore */
  }
};

const isServerWallet = (v: unknown): v is ServerWallet => {
  const o = v as Partial<ServerWallet> | null;
  return !!o && typeof o.balance === 'number' && Array.isArray(o.entitlements) && !!o.daily;
};

export const createSyncedWallet = (o: SyncedWalletOptions): SyncedWallet => {
  const { local, storage, account } = o;
  const now = o.now ?? (() => Date.now());
  const rand = o.random ?? Math.random;
  const changed = (): void => o.onChange?.();

  let snap: Snapshot | null = readJson<Snapshot>(storage, SNAP_KEY);
  // A snapshot of another account (the device was reset server-side) is not ours.
  if (snap && account?.userId && snap.userId !== account.userId) snap = null;
  if (!account) snap = null;
  let queue: PendingOp[] = readJson<PendingOp[]>(storage, QUEUE_KEY) ?? [];
  let syncing: Promise<boolean> | null = null;

  const saveSnap = (): void => writeJson(storage, SNAP_KEY, snap);
  const saveQueue = (): void => writeJson(storage, QUEUE_KEY, queue);
  const newId = (): string =>
    `${now().toString(36)}-${Math.floor(rand() * 2 ** 32).toString(36)}${Math.floor(rand() * 2 ** 32).toString(36)}`;

  const rollDay = (s: Snapshot): Snapshot => {
    const d = utcDay(now());
    if (s.daily.day === d) return s;
    s.daily = { ...s.daily, day: d, botMatchesRewardedToday: 0, onlineFirstWinClaimedToday: false };
    return s;
  };

  const adopt = (userId: string, w: ServerWallet): void => {
    snap = {
      userId,
      balance: w.balance,
      owned: [...w.entitlements],
      daily: { ...w.daily, day: utcDay(now()) },
    };
    // Re-apply still-queued optimistic mutations on top of the server truth (they are not confirmed yet).
    for (const op of queue) {
      if (op.kind === 'unlock' && !snap.owned.includes(op.sku)) {
        const price = priceOf(op.sku);
        if (price !== null && snap.balance >= price) {
          snap.balance -= price;
          snap.owned.push(op.sku);
        }
      } else if (op.kind === 'bot') {
        const amount = botMatchReward({
          won: op.won,
          durationSec: op.durationSec,
          rewardedToday: snap.daily.botMatchesRewardedToday,
        });
        if (amount > 0) {
          snap.balance += amount;
          snap.daily.botMatchesRewardedToday++;
        }
      }
    }
    saveSnap();
    changed();
  };

  const flush = async (): Promise<void> => {
    if (!account || !snap) return;
    while (queue.length) {
      const op = queue[0]!;
      const r =
        op.kind === 'unlock'
          ? await account.call('POST', '/shop/unlock', { sku: op.sku, idem_key: op.idem })
          : await account.call('POST', '/rewards/bot', {
              match_id: op.matchId,
              won: op.won,
              duration_sec: op.durationSec,
            });
      if (r.status === 0 || r.status >= 500 || r.status === 401) return; // try again on the next sync
      // Settled (accepted, refused as unaffordable / unknown, or a replay): drop it and take the server's word.
      queue.shift();
      saveQueue();
      if (isServerWallet(r.body)) adopt(snap.userId, r.body);
    }
  };

  const doSync = async (): Promise<boolean> => {
    if (!account) return false;
    if (!(await account.ensure())) return false;
    const me = await account.call<ServerWallet>('GET', '/me');
    if (me.status !== 200 || !isServerWallet(me.body) || !account.userId) return false;
    const userId = account.userId;
    if (!snap || snap.userId !== userId) {
      if (!me.body.migrated) {
        // First sync of this account: import the pre-account local wallet once (capped by the server).
        const m = await account.call<ServerWallet>('POST', '/wallet/migrate', {
          balance: local.getBalance(),
          owned: localOwned(local),
        });
        if (m.status !== 200 || !isServerWallet(m.body)) return false;
        adopt(userId, m.body);
      } else {
        adopt(userId, me.body);
      }
    } else {
      adopt(userId, me.body);
    }
    await flush();
    return true;
  };

  const sync = (): Promise<boolean> => {
    if (!syncing) {
      syncing = doSync()
        .catch(() => false)
        .then((ok) => {
          syncing = null;
          return ok;
        });
    }
    return syncing;
  };

  const enqueue = (op: PendingOp): void => {
    queue.push(op);
    saveQueue();
    void sync();
  };

  // ── LocalWallet surface ────────────────────────────────────────────────────────────────────────────────

  const getBalance = (): number => (snap ? snap.balance : local.getBalance());

  const owns = (sku: string): boolean => (snap ? isFree(sku) || snap.owned.includes(sku) : local.owns(sku));

  /** Server mode: only the server credits coins (rewards, purchases). Local mode: as before. */
  const credit: LocalWallet['credit'] = (amount, reason) =>
    snap ? snap.balance : local.credit(amount, reason);

  /** Generic spends are not a server operation (only unlocks spend): local mode only. */
  const spend: LocalWallet['spend'] = (amount, reason) => (snap ? false : local.spend(amount, reason));

  const unlock: LocalWallet['unlock'] = (sku, priceCoins) => {
    if (!snap) return local.unlock(sku, priceCoins);
    if (owns(sku)) return true;
    const price = priceOf(sku) ?? priceCoins;
    if (snap.balance < price) return false;
    snap.balance -= price;
    snap.owned.push(sku);
    saveSnap();
    enqueue({ kind: 'unlock', sku, idem: newId() });
    return true;
  };

  const getDailyStatus = (): WalletDailyStatus => {
    if (!snap) return local.getDailyStatus();
    const d = rollDay(snap).daily;
    return {
      botMatchesRewardedToday: d.botMatchesRewardedToday,
      onlineWinStreak: d.onlineWinStreak,
      onlineFirstWinClaimedToday: d.onlineFirstWinClaimedToday,
    };
  };

  const rewardBotMatch: LocalWallet['rewardBotMatch'] = (won, durationSec) => {
    if (!snap) return local.rewardBotMatch(won, durationSec);
    const d = rollDay(snap).daily;
    const amount = botMatchReward({ won, durationSec, rewardedToday: d.botMatchesRewardedToday });
    if (amount > 0) {
      snap.balance += amount;
      d.botMatchesRewardedToday++;
      saveSnap();
      enqueue({ kind: 'bot', matchId: newId(), won, durationSec: Math.round(durationSec) });
    }
    return amount;
  };

  const rewardOnlineMatch: SyncedWallet['rewardOnlineMatch'] = (won, opts = {}) => {
    if (!snap) return local.rewardOnlineMatch(won, opts);
    // The server pays this itself; show its expected amount now (confirmOnlineReward corrects it).
    if (opts.authed === false) return 0;
    if (opts.durationSec !== undefined && opts.durationSec < ONLINE_MIN_DURATION_SEC) return 0;
    const d = rollDay(snap).daily;
    const firstWinOfDay = won && !d.onlineFirstWinClaimedToday;
    return onlineMatchReward({
      won,
      priorWinStreak: d.onlineWinStreak,
      firstWinOfDay,
      forfeit: opts.forfeit ?? false,
    });
  };

  const confirmOnlineReward = async (matchId: string): Promise<number | null> => {
    if (!snap || !account) return null;
    // The lobby pays right after match_result; give the Worker a moment, then look the game up in the ledger.
    for (const waitMs of [1200, 3000]) {
      await new Promise<void>((r) => setTimeout(() => r(), waitMs));
      const me = await account.call<ServerWallet>('GET', '/me');
      if (me.status !== 200 || !isServerWallet(me.body) || !account.userId) continue;
      adopt(account.userId, me.body);
      const rows = (me.body.recent ?? []).filter(
        (x) => x.ref_type === 'match' && typeof x.ref_id === 'string' && x.ref_id.startsWith(`${matchId}:`),
      );
      if (rows.length) return rows.reduce((s, x) => Math.max(s, x.delta), 0);
    }
    return 0;
  };

  const creditAppleTransaction = async (jws: string): Promise<PurchaseCreditResult> => {
    if (!account) return { status: 'local' };
    // Coins must land in the account (never the soon-replaced local wallet): sync first if not yet in server mode.
    if (!snap && !(await sync())) return { status: 'retry' };
    if (!snap) return { status: 'retry' };
    const r = await account.call<ServerWallet & { credited?: boolean; coins?: number; error?: string }>(
      'POST',
      '/iap/apple',
      { jws },
    );
    if (r.status === 200 && isServerWallet(r.body) && account.userId) {
      const b = r.body;
      adopt(account.userId, b);
      return { status: b.credited ? 'credited' : 'duplicate', coins: Number(b.coins ?? 0) };
    }
    if (r.status === 0 || r.status === 401 || r.status === 429 || r.status >= 500) return { status: 'retry' };
    return { status: 'rejected', error: String((r.body as { error?: string } | null)?.error ?? r.status) };
  };

  const reset = (): void => {
    snap = null;
    queue = [];
    try {
      storage?.setItem(SNAP_KEY, '');
      storage?.setItem(QUEUE_KEY, '');
    } catch {
      /* ignore */
    }
    local.reset();
    changed();
  };

  const resetAfterDelete = async (): Promise<void> => {
    reset();
    // Pull the new guest's empty wallet when online; offline stays on the fresh local wallet.
    await sync().catch(() => false);
  };

  return {
    creditAppleTransaction,
    getBalance,
    credit,
    spend,
    owns,
    unlock,
    getDailyStatus,
    rewardBotMatch,
    rewardOnlineMatch,
    reset,
    mode: () => (snap ? 'server' : 'local'),
    sync,
    confirmOnlineReward,
    resetAfterDelete,
  };
};

const CATALOG_PRICE = new Map<string, number>([
  ...CHARACTER_IDS.map((id): [string, number] => [id, CHARACTER_CATALOG[id].priceCoins]),
  ...BALL_SKIN_IDS.map((id): [string, number] => [id, BALL_SKIN_CATALOG[id].priceCoins]),
]);
/** Client-side catalog price (the server re-checks its own catalog); null for an unknown sku. */
const priceOf = (sku: string): number | null => CATALOG_PRICE.get(sku) ?? null;
